import type { CoalescedTodos } from "@agent-ui/features/chat/components/chat-elements/chat-message/tool-rows/TodoToolRow";
import type {
  OpencodeMessagePart,
  OpencodeTodoItem,
  OpencodeToolPart,
  SessionMessageWithParts,
} from "schemas/generated/opencode";

import React from "react";

import { EditedPagesCarousel } from "@agent-ui/features/chat/components/chat-elements/chat-message/EditedPagesCarousel";
import { OpencodeIntegrationStatusToolBlock } from "@agent-ui/features/chat/components/chat-elements/chat-message/IntegrationStatusToolBlock";
import { OpencodeToolSectionCard } from "@agent-ui/features/chat/components/chat-elements/chat-message/OpencodeToolSectionCard";
import { QuestionToolBlock } from "@agent-ui/features/chat/components/chat-elements/chat-message/QuestionToolBlock";
import { TextMessage } from "@agent-ui/features/chat/components/chat-elements/chat-message/TextMessageItem";
import { ChatActions } from "@agent-ui/features/chat/components/chat-elements/ChatActions";
import { ChatDebugPanel } from "@agent-ui/features/chat/components/chat-elements/ChatDebugPanel";
import { useSessionUuid } from "@agent-ui/features/chat/hooks/useSessionUuid";
import { getEditedPages } from "@agent-ui/features/chat/utils/editedPages";
import {
  isIntegrationStatusTool,
  isQuestionTool,
  isStatefulTool,
} from "@agent-ui/features/chat/utils/toolDisplay";
import LogoBadge from "@v2/components/LogoBadge";
import shouldShowV2Debuggers from "@v2/utils/shouldShowV2Debuggers";
import uniqBy from "lodash-es/uniqBy";
import { Ban, Dot } from "lucide-react";
import { opencodeTodoToolInputSchema } from "schemas/opencode";

const AssistantTypingIndicator = ({
  statusText,
}: {
  statusText?: string | null;
}) => {
  return (
    <div className="flex items-center gap-1">
      {statusText && (
        <span className="text-sm text-muted text-shimmer">{statusText}</span>
      )}
      <div className="flex -space-x-2.5">
        <Dot className="h-5 w-5 animate-typing-dot-bounce" />
        <Dot className="h-5 w-5 animate-typing-dot-bounce [animation-delay:90ms]" />
        <Dot className="h-5 w-5 animate-typing-dot-bounce [animation-delay:180ms]" />
      </div>
    </div>
  );
};

/**
 * Splits a list of assistant parts into four groups for rendering in
 * OpencodeAssistantGroupItem:
 *
 * - `preToolTextParts` — Text parts that appear before any tool calls. Rendered
 *   as standalone TextMessage blocks above the tool section card.
 * - `postToolTextParts` — Text parts that appear after the last tool call (i.e.
 *   the assistant's final response). Rendered as TextMessage blocks below the
 *   tool section card.
 * - `toolParts` — Non-stateful tool calls (Read, Edit, Bash, etc.). Rendered as
 *   individual ToolRow items inside OpencodeToolSectionCard.
 * - `coalescedTodos` — All stateful TodoWrite/TodoRead parts merged into a
 *   single plan state. These are separated from regular tool parts because each
 *   call represents the current plan state (not a discrete action), so N raw
 *   parts coalesce into one TodoToolRow inside OpencodeToolSectionCard.
 *
 * @author Max 2026-02-17
 */
const splitAssistantParts = (parts: OpencodeMessagePart[]) => {
  const firstToolPartIndex = parts.findIndex(
    (part) => part.partData.type === "tool",
  );
  if (firstToolPartIndex === -1) {
    return {
      preToolTextParts: parts.filter(
        (
          part,
        ): part is OpencodeMessagePart & {
          partData: { type: "text"; text: string };
        } => part.partData.type === "text",
      ),
      toolParts: [] as OpencodeToolPart[],
      questionToolParts: [] as OpencodeToolPart[],
      integrationStatusToolParts: [] as OpencodeToolPart[],
      postToolTextParts: [] as (OpencodeMessagePart & {
        partData: { type: "text"; text: string };
      })[],
      coalescedTodos: null as CoalescedTodos | null,
    };
  }

  const lastToolPartIndex = parts.findLastIndex(
    (part) => part.partData.type === "tool",
  );

  const allToolParts = parts
    .slice(firstToolPartIndex, lastToolPartIndex + 1)
    .filter(
      (part): part is OpencodeMessagePart & { partData: OpencodeToolPart } => {
        return part.partData.type === "tool";
      },
    )
    .map((part) => part.partData);

  const todoToolParts = allToolParts.filter((part) => {
    return isStatefulTool(part.tool);
  });

  const questionToolParts = allToolParts.filter((part) => {
    return isQuestionTool(part.tool);
  });

  const integrationStatusToolParts = allToolParts.filter((part) => {
    return isIntegrationStatusTool(part.tool);
  });

  const regularToolParts = allToolParts.filter((part) => {
    return (
      !isStatefulTool(part.tool) &&
      !isQuestionTool(part.tool) &&
      !isIntegrationStatusTool(part.tool)
    );
  });

  /**
   * NOTE (Max, 2026-02-17):
   * Replay todo tool parts chronologically to build the final plan state.
   * Each TodoWrite call either resets the list (merge=false or absent) or
   * patches it (merge=true, upserts by id). We keep the latest part's
   * metadata (status, timing) so TodoToolRow can show the correct status icon
   * and duration on the tool row (e.g. if it's fully completed).
   *
   * The coalescedTodos list will contain the most up-to-date list of todos.
   * Then, we can just display a <TodoToolRow> component, which, when expanded,
   * shows the list of todos & their status
   */
  let coalescedTodos: CoalescedTodos | null = null;
  if (todoToolParts.length > 0) {
    let mergedTodos: OpencodeTodoItem[] = [];
    for (const part of todoToolParts) {
      const parsed = opencodeTodoToolInputSchema.safeParse(part.state.input);
      if (!parsed.success) {
        // NOTE (Max, 2026-02-17): TodoRead parts don't have a todos field,
        // so they won't match the schema. Skip them.
        continue;
      }
      const { todos, merge } = parsed.data;

      if (merge) {
        mergedTodos = uniqBy([...todos, ...mergedTodos], (t) => t.id);
      } else {
        mergedTodos = [...todos];
      }
    }

    const latestTodoPart = todoToolParts[todoToolParts.length - 1]!;
    coalescedTodos = { todos: mergedTodos, latestPart: latestTodoPart };
  }

  return {
    preToolTextParts: parts.slice(0, firstToolPartIndex).filter(
      (
        part,
      ): part is OpencodeMessagePart & {
        partData: { type: "text"; text: string };
      } => part.partData.type === "text",
    ),
    toolParts: regularToolParts,
    questionToolParts,
    integrationStatusToolParts,
    postToolTextParts: parts.slice(lastToolPartIndex + 1).filter(
      (
        part,
      ): part is OpencodeMessagePart & {
        partData: { type: "text"; text: string };
      } => part.partData.type === "text",
    ),
    coalescedTodos,
  };
};

export const OpencodeAssistantGroupItem = ({
  sessionMessages,
  subagentMessages,
  isChatLoading,
  isLastAssistantMessage,
  isCancelled,
  statusText,
}: {
  sessionMessages: SessionMessageWithParts[];
  subagentMessages?: SessionMessageWithParts[];
  isChatLoading: boolean;
  isLastAssistantMessage: boolean;
  isCancelled: boolean;
  statusText?: string | null;
}) => {
  const sessionUuid = useSessionUuid();
  const allParts = React.useMemo(
    () => sessionMessages.flatMap((sessionMessage) => sessionMessage.parts),
    [sessionMessages],
  );
  const editedPages = React.useMemo(() => getEditedPages(allParts), [allParts]);

  if (sessionMessages.length === 0) {
    return null;
  }

  if (allParts.length === 0) {
    // NOTE (Ben, 2026-03-09, REPL-24858): When the assistant message exists but
    // has no parts yet, show the typing indicator inline instead of returning null.
    // This keeps the same DOM container (logo + content area) so there's no layout
    // shift when the first text part arrives and replaces the dots.
    if (isChatLoading && isLastAssistantMessage) {
      return (
        <div className="flex flex-row items-start w-full gap-3">
          <LogoBadge className="h-6 w-6 shrink-0" aria-hidden />
          <AssistantTypingIndicator statusText={statusText} />
        </div>
      );
    }
    return null;
  }

  const lastMessage = sessionMessages[sessionMessages.length - 1];
  if (!lastMessage) {
    return null;
  }

  const {
    preToolTextParts,
    toolParts,
    questionToolParts,
    integrationStatusToolParts,
    postToolTextParts,
    coalescedTodos,
  } = splitAssistantParts(allParts);
  const hasToolContent =
    toolParts.length > 0 || (coalescedTodos?.todos.length ?? 0) > 0;
  const isStreaming = Boolean(isChatLoading && isLastAssistantMessage);

  return (
    <div className="flex flex-row items-start w-full gap-3">
      <LogoBadge className="h-6 w-6 shrink-0" aria-hidden />
      <div className="flex flex-col w-full min-w-0 gap-1">
        {preToolTextParts.map((part) => (
          <TextMessage
            key={part.id}
            text={part.partData.text}
            role="assistant"
          />
        ))}

        {sessionUuid &&
          questionToolParts.map((part) => (
            <QuestionToolBlock
              key={part.id}
              toolPart={part}
              sessionUuid={sessionUuid}
            />
          ))}

        {hasToolContent && (
          <OpencodeToolSectionCard
            toolParts={toolParts}
            coalescedTodos={coalescedTodos}
            isStreaming={isStreaming}
            opencodeSessionUuid={lastMessage.message.opencodeSessionUuid}
            opencodeMessageUuid={lastMessage.message.id}
            subagentMessages={subagentMessages}
          />
        )}

        {integrationStatusToolParts.map((part) => (
          <OpencodeIntegrationStatusToolBlock
            key={part.callID}
            toolPart={part}
          />
        ))}

        {postToolTextParts.map((part) => (
          <TextMessage
            key={part.id}
            text={part.partData.text}
            role="assistant"
          />
        ))}

        <EditedPagesCarousel pages={editedPages} isStreaming={isStreaming} />

        {isCancelled ? (
          <span className="flex items-center gap-1.5 text-sm text-muted">
            <Ban className="h-3.5 w-3.5" />
            This request has been canceled
          </span>
        ) : (
          <ChatActions
            sessionMessages={sessionMessages}
            isChatLoading={isChatLoading}
            isLastAssistantGroup={isLastAssistantMessage}
          />
        )}

        {shouldShowV2Debuggers() && (
          <ChatDebugPanel
            opencodeSessionId={lastMessage.message.opencodeSessionId}
            sessionUuid={sessionUuid}
            messageCreatedAt={lastMessage.message.createdAt}
          />
        )}
      </div>
    </div>
  );
};
